import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, CheckCircle2, Phone, Award, Users, HandHeart, MessagesSquare, BadgeCheck, UserCheck, Clock, Star, Trophy } from "lucide-react";
import { SERVICES, STATS, CREDENTIALS, WHY_CHOOSE, ADVISOR, BLOG_POSTS, COMPANY } from "../data/mock";
import { useRevealGroup, useCounter } from "../hooks/useReveal";
import { SectionHeading, PillBadge, ServiceCard, TestimonialsSection, CTASection } from "../components/Shared";
import { useQuote } from "../components/QuoteModal";

const WHY_ICONS = { MessagesSquare, BadgeCheck, UserCheck, Clock, Star, Trophy };

function StatItem({ stat, index }) {
  const [ref, value] = useCounter(stat.value);
  return (
    <div ref={ref} data-reveal style={{ transitionDelay: `${index * 90}ms` }} className="text-center lg:text-left">
      <p className="font-heading text-4xl font-extrabold tracking-tight text-[#006FB9] sm:text-5xl">
        {value}
        <span className="text-[#54AEDB]">{stat.suffix}</span>
      </p>
      <p className="mt-2 text-sm font-medium text-[#526A7D]">{stat.label}</p>
    </div>
  );
}

export default function Home() {
  const revealRef = useRevealGroup();
  const { openQuote } = useQuote();

  return (
    <div ref={revealRef}>
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1531983412531-1f49a365ffed?q=80&w=1920"
            alt="Happy family on the beach"
            className="kenburns h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#062A4D]/85 via-[#073B67]/55 to-transparent" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 pb-32 pt-28 lg:pb-44 lg:pt-40">
          <div className="hero-stagger max-w-2xl">
            <PillBadge light>We Are Abhi Insurance</PillBadge>
            <h1 className="mt-5 font-heading text-5xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl">
              Protecting You
              <span className="block text-[#B9DFF3]">Always</span>
            </h1>
            <p className="mt-6 max-w-xl text-[16.5px] leading-relaxed text-[#D9ECF7]">
              Since {COMPANY.since}, we've helped Canadians, immigrants and visitors find the right coverage — Super Visa, Visitor Visa, Life, Critical Illness and Disability insurance — at the best possible rates.
            </p>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
              <Link to="/services" className="btn-light group" data-testid="hero-explore">
                Explore More <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <button onClick={() => openQuote()} className="btn-outline-light group" data-testid="hero-get-quote">
                Get a Free Quote <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-[#D4E9F4]">
              <span className="flex items-center gap-2"><Award className="h-4 w-4 text-[#B9DFF3]" /> 8x 'Top of the Table'</span>
              <span className="flex items-center gap-2"><Users className="h-4 w-4 text-[#B9DFF3]" /> MDRT Member since 2010</span>
              <span className="flex items-center gap-2"><HandHeart className="h-4 w-4 text-[#B9DFF3]" /> 100% Satisfaction Guaranteed</span>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#F4F7FB] to-transparent" />
      </section>

      {/* ============ STATS ============ */}
      <section className="bg-[#F4F7FB] py-16 lg:py-20">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 lg:grid-cols-2">
          <div data-reveal>
            <h2 className="font-heading text-3xl font-extrabold leading-[1.15] tracking-tight text-[#0B2748] sm:text-4xl">
              Unmatched service, unwavering commitment, <span className="text-[#0785C7]">always for you.</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-8 lg:gap-10">
            {STATS.map((s, i) => (
              <StatItem key={s.label} stat={s} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ============ ABOUT ============ */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2">
          <div className="relative" data-reveal>
            <div className="overflow-hidden rounded-3xl">
              <img src="https://images.pexels.com/photos/32206234/pexels-photo-32206234.jpeg" alt="Happy family at home" className="h-[480px] w-full object-cover transition-transform duration-700 hover:scale-105" />
            </div>
            <div className="float-slow absolute -bottom-8 -right-4 rounded-2xl bg-[#006FB9] p-6 text-white shadow-[0_20px_50px_rgba(0,111,185,0.35)] sm:right-8">
              <p className="font-heading text-4xl font-extrabold">25+</p>
              <p className="mt-1 max-w-[140px] text-sm leading-snug text-[#C5DEEC]">Years Trusted Experience since {COMPANY.since}</p>
            </div>
          </div>
          <div data-reveal style={{ transitionDelay: "120ms" }}>
            <PillBadge>About Us</PillBadge>
            <h2 className="mt-4 font-heading text-3xl font-extrabold leading-[1.15] tracking-tight text-[#0B2748] sm:text-4xl lg:text-[42px]">
              Because Your <span className="text-[#0785C7]">Peace Matters</span>
            </h2>
            <p className="mt-5 text-[15.5px] leading-relaxed text-[#526A7D]">
              Life is full of challenges and unexpected events. Before anything unpredictable occurs, protect yourself and your family from financial loss with the right insurance coverage. As your personal insurance advisor in Surrey, BC, we take the worry and guesswork out of your financial protection.
            </p>
            <ul className="mt-7 space-y-3.5">
              {["Comprehensive protection tailored to your circumstances", "Customizable plans compared across Canada's top insurers", "Fast claims support & same-day partial refunds", "Up to 45% discounted rates — guaranteed lowest premiums"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-[15px] font-medium text-[#193B58]">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#0785C7]" /> {t}
                </li>
              ))}
            </ul>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/about" className="btn-primary group" data-testid="about-read-more">
                Read More <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a href={COMPANY.phoneHref} className="flex items-center gap-3 text-[#0B2748]">
                <span className="grid h-11 w-11 place-items-center rounded-full bg-[#E2F2FA]"><Phone className="h-5 w-5 text-[#006FB9]" /></span>
                <span className="text-sm"><span className="block text-[#526A7D]">Call us anytime</span><span className="font-heading font-bold">{COMPANY.phone}</span></span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SERVICES ============ */}
      <section className="bg-[#F4F7FB] py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading
            badge="Our Services"
            title={<>Reliable Insurance, <span className="text-[#0785C7]">Anytime, Anywhere</span></>}
            subtitle="Whether you were born in Canada, are new to the country, or just visiting — we'll find the right coverage for the right price."
          />
          <div className="mt-14 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <ServiceCard key={s.slug} service={s} index={i} />
            ))}
            {/* CTA tile */}
            <div data-reveal style={{ transitionDelay: "400ms" }} className="group relative flex flex-col justify-between overflow-hidden rounded-2xl bg-[#006FB9] p-8 text-white shadow-[0_2px_16px_rgba(0,111,185,0.15)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_24px_48px_rgba(0,111,185,0.3)]">
              <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#B9DFF3]/15 transition-transform duration-700 group-hover:scale-150" />
              <div className="relative">
                <h3 className="font-heading text-2xl font-bold leading-tight">Not sure which coverage you need?</h3>
                <p className="mt-4 text-sm leading-relaxed text-[#C5DEEC]">
                  Give us a call for a free consultation — our expert advisors are available 7 days a week.
                </p>
              </div>
              <button onClick={() => openQuote()} className="btn-light relative mt-8 w-fit" data-testid="services-cta-quote">
                Get Free Advice <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ============ WHY CHOOSE US ============ */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading
            badge="Why Choose Us"
            title={<>Insurance Made <span className="text-[#0785C7]">Easy & Honest</span></>}
            subtitle="Nearly 25 years of answering every insurance question you can imagine — with some of the best, most reliable insurance companies in British Columbia."
          />
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_CHOOSE.map((item, i) => {
              const Icon = WHY_ICONS[item.icon];
              return (
                <div key={item.title} data-reveal style={{ transitionDelay: `${i * 70}ms` }} className="tilt-card group rounded-2xl border border-[#E1ECF3] bg-white p-8 transition-all duration-500 hover:border-transparent hover:shadow-[0_20px_44px_rgba(0,111,185,0.12)]">
                  <span className="grid h-13 w-13 h-[52px] w-[52px] place-items-center rounded-2xl bg-[#E2F2FA] transition-all duration-500 group-hover:rotate-6 group-hover:bg-[#006FB9]">
                    <Icon className="h-6 w-6 text-[#006FB9] transition-colors duration-500 group-hover:text-[#B9DFF3]" />
                  </span>
                  <h3 className="mt-5 font-heading text-lg font-bold text-[#0B2748]">{item.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-[#526A7D]">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ CREDENTIALS MARQUEE ============ */}
      <section className="border-y border-[#E1ECF3] bg-[#F4F7FB] py-6">
        <div className="marquee-mask overflow-hidden">
          <div className="marquee flex w-max items-center gap-4">
            {[...CREDENTIALS, ...CREDENTIALS].map((c, i) => (
              <span key={i} className="flex items-center gap-2.5 whitespace-nowrap rounded-full bg-white px-6 py-2.5 text-sm font-semibold text-[#193B58] shadow-sm">
                <Award className="h-4 w-4 text-[#0785C7]" /> {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ============ ADVISOR ============ */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2">
          <div data-reveal>
            <PillBadge>Meet Your Advisor</PillBadge>
            <h2 className="mt-4 font-heading text-3xl font-extrabold leading-[1.15] tracking-tight text-[#0B2748] sm:text-4xl lg:text-[42px]">
              Professional Advice, <span className="text-[#0785C7]">Personal Care</span>
            </h2>
            <p className="mt-5 text-[15.5px] leading-relaxed text-[#526A7D]">{ADVISOR.bio}</p>
            <div className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {ADVISOR.highlights.map((h) => (
                <span key={h} className="flex items-center gap-2.5 rounded-xl bg-[#F4F7FB] px-4 py-3 text-sm font-semibold text-[#193B58]">
                  <Trophy className="h-4 w-4 shrink-0 text-[#0785C7]" /> {h}
                </span>
              ))}
            </div>
            <Link to="/contact" className="btn-primary group mt-9" data-testid="advisor-contact">
              Book a Free Consultation <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="relative" data-reveal style={{ transitionDelay: "120ms" }}>
            <div className="overflow-hidden rounded-3xl">
              <img src={ADVISOR.image} alt={ADVISOR.name} className="h-[500px] w-full object-cover object-top transition-transform duration-700 hover:scale-105" />
            </div>
            <div className="absolute bottom-6 left-6 right-6 rounded-2xl bg-white/95 p-5 shadow-lg backdrop-blur">
              <p className="font-heading text-lg font-bold text-[#0B2748]">{ADVISOR.name}</p>
              <p className="text-sm text-[#526A7D]">{ADVISOR.role}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <TestimonialsSection />

      {/* ============ MID CTA BAND ============ */}
      <section className="relative overflow-hidden">
        <img src="https://images.pexels.com/photos/37877428/pexels-photo-37877428.jpeg" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#062A4D]/80" />
        <div className="relative mx-auto max-w-4xl px-6 py-24 text-center">
          <div data-reveal>
            <PillBadge light>Abhi, Protecting You Always</PillBadge>
            <h2 className="mt-5 font-heading text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl">
              Ready to protect the people you love the most?
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-[15.5px] leading-relaxed text-[#D4E9F4]">
              Speak with a licensed advisor today. Free consultation, honest advice, and coverage matched to your budget.
            </p>
            <button onClick={() => openQuote()} className="btn-light group mx-auto mt-9" data-testid="mid-cta-quote">
              Request a Free Consultation <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </section>

      {/* ============ BLOG PREVIEW ============ */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading
            badge="Our Blog"
            title={<>Insurance Insights, <span className="text-[#0785C7]">Made Simple</span></>}
            subtitle="Practical, plain-language guides to help you make confident decisions about your coverage."
          />
          <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-3">
            {BLOG_POSTS.slice(0, 3).map((post, i) => (
              <Link key={post.slug} to={`/blog/${post.slug}`} data-reveal style={{ transitionDelay: `${i * 90}ms` }} data-testid={`blog-card-${post.slug}`} className="tilt-card group overflow-hidden rounded-2xl bg-white shadow-[0_2px_16px_rgba(0,111,185,0.07)] transition-all duration-500 hover:shadow-[0_24px_48px_rgba(0,111,185,0.15)]">
                <div className="relative h-48 overflow-hidden">
                  <img src={post.image} alt={post.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <span className="absolute left-4 top-4 rounded-full bg-white/95 px-3.5 py-1 text-xs font-bold text-[#006FB9]">{post.category}</span>
                </div>
                <div className="p-7">
                  <p className="text-xs font-medium text-[#70879A]">{post.date} · {post.readTime}</p>
                  <h3 className="mt-2.5 font-heading text-lg font-bold leading-snug text-[#0B2748] transition-colors duration-300 group-hover:text-[#0785C7]">{post.title}</h3>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#006FB9]">
                    Read Article <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-12 text-center" data-reveal>
            <Link to="/blog" className="btn-outline group" data-testid="view-all-posts">
              View All Insights <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <CTASection />
    </div>
  );
}
